import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users, Target, Archive, MessageCircle, ChevronRight, Copy, Check,
  TrendingUp, UserPlus, ClipboardCheck, ExternalLink, Power, Settings,
} from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Card, Spinner, Badge, EmptyState } from '../../../components/ui'

interface Stats {
  members_total: number
  members_week: number
  members_month: number
  missions_active: number
  missions_completed_week: number
  missions_completed_month: number
  media_total: number
  messages_week: number
  channels_total: number
  pending_submissions: number
}

export default function CommunityDashboardPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [activating, setActivating] = useState(false)
  const [activated, setActivated] = useState(false)
  const [stats, setStats] = useState<Stats | null>(null)
  const [communityUrl, setCommunityUrl] = useState('')
  const [settings, setSettings] = useState<any>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => { fetchDashboard() }, [])

  async function fetchDashboard() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/community/dashboard'))
      setActivated(res.data.activated)
      setStats(res.data.data)
      setCommunityUrl(res.data.community_url || '')
      setSettings(res.data.settings)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function handleActivate() {
    setActivating(true)
    try {
      await axios.post(buildApiUrl('/v1/community/activate'))
      toast('success', 'Comunidade ativada!')
      await fetchDashboard()
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro ao ativar')
    } finally {
      setActivating(false)
    }
  }

  async function handleCopyUrl() {
    await navigator.clipboard.writeText(communityUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (!activated) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comunidade</h1>
          <p className="text-gray-500 mt-1">Engaje seus apoiadores com missões, conteúdos e discussões</p>
        </div>

        <Card>
          <EmptyState
            icon={<Users className="w-8 h-8" />}
            title="Comunidade não ativada"
            description="Ative a comunidade para criar missões, importar conteúdos e engajar seus apoiadores. Um canal 'Geral' será criado automaticamente."
            action={
              <Button onClick={handleActivate} loading={activating}>
                <Power className="w-4 h-4 mr-2" />
                Ativar Comunidade
              </Button>
            }
          />
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comunidade</h1>
          <p className="text-gray-500 mt-1">{settings?.app_name || 'Visão geral da comunidade'}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/community/settings')}>
          <Settings className="w-4 h-4 mr-1.5" />
          Configurar
        </Button>
      </div>

      {/* Link da comunidade */}
      {communityUrl && (
        <div className="flex items-center gap-3 px-4 py-3 bg-brand-50 border border-brand-200 rounded-xl">
          <ExternalLink className="w-4 h-4 text-brand-500 shrink-0" />
          <a href={communityUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-600 font-medium truncate hover:underline">
            {communityUrl}
          </a>
          <button
            onClick={handleCopyUrl}
            className="ml-auto flex items-center gap-1 px-3 py-1 text-xs font-medium text-brand-600 bg-white border border-brand-200 rounded-lg hover:bg-brand-100 transition-colors shrink-0"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard
            icon={<Users className="w-5 h-5" />}
            label="Membros"
            value={stats.members_total}
            color="brand"
            onClick={() => navigate('/community/members')}
          />
          <StatCard
            icon={<Target className="w-5 h-5" />}
            label="Missões ativas"
            value={stats.missions_active}
            color="info"
            badge={stats.pending_submissions > 0 ? `${stats.pending_submissions} pendentes` : undefined}
            onClick={() => navigate('/community/missions')}
          />
          <StatCard
            icon={<Archive className="w-5 h-5" />}
            label="Mídias no arsenal"
            value={stats.media_total}
            color="success"
            onClick={() => navigate('/community/arsenal')}
          />
          <StatCard
            icon={<MessageCircle className="w-5 h-5" />}
            label="Mensagens esta semana"
            value={stats.messages_week}
            color="warning"
            onClick={() => navigate('/community/channels')}
          />
        </div>
      )}

      {/* Resumo semanal/mensal */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-success-500/10 text-success-500 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-gray-800">Missões completadas</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.missions_completed_week}</p>
                <p className="text-xs text-gray-500">Esta semana</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.missions_completed_month}</p>
                <p className="text-xs text-gray-500">Este mês</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center">
                <UserPlus className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-gray-800">Novos membros</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.members_week}</p>
                <p className="text-xs text-gray-500">Esta semana</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.members_month}</p>
                <p className="text-xs text-gray-500">Este mês</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Ações rápidas */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Ações rápidas</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <ActionCard icon={<Target className="w-5 h-5" />} label="Criar missão" description="Nova missão digital ou presencial" onClick={() => navigate('/community/missions')} />
          <ActionCard icon={<Archive className="w-5 h-5" />} label="Importar mídia" description="Adicionar imagem, vídeo ou texto" onClick={() => navigate('/community/arsenal')} />
          <ActionCard icon={<MessageCircle className="w-5 h-5" />} label="Ver canais" description={`${stats?.channels_total || 0} canais ativos`} onClick={() => navigate('/community/channels')} />
          <ActionCard icon={<Users className="w-5 h-5" />} label="Ver membros" description={`${stats?.members_total || 0} membros cadastrados`} onClick={() => navigate('/community/members')} />
        </div>
      </div>
    </div>
  )
}

function StatCard({ icon, label, value, color, badge, onClick }: {
  icon: React.ReactNode; label: string; value: number; color: string; badge?: string; onClick?: () => void
}) {
  const colorMap: Record<string, string> = {
    brand: 'bg-brand-500/10 text-brand-500',
    info: 'bg-info-500/10 text-info-500',
    success: 'bg-success-500/10 text-success-500',
    warning: 'bg-warning-500/10 text-warning-500',
    error: 'bg-error-500/10 text-error-500',
  }

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl border border-gray-200 p-4 cursor-pointer hover:shadow-sm transition-shadow"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorMap[color] || colorMap.brand}`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {badge && (
            <Badge variant="warning" size="sm">{badge}</Badge>
          )}
          <ChevronRight className="w-4 h-4 text-gray-300" />
        </div>
      </div>
    </div>
  )
}

function ActionCard({ icon, label, description, onClick }: {
  icon: React.ReactNode; label: string; description: string; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:shadow-sm hover:border-brand-200 transition-all text-left group"
    >
      <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-500 flex items-center justify-center group-hover:bg-brand-100 transition-colors shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800">{label}</p>
        <p className="text-xs text-gray-500 truncate">{description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-400 transition-colors shrink-0" />
    </button>
  )
}
